import * as errors from './common/errors';
import * as helpers from './common/helpers';
import type {
  AsyncProperties,
  EventProperties,
  JsonValue,
  PropertyGetter,
  PropertyResolver,
  ResolvableProperties,
} from './common/types';
import {
  ALWAYS_ALLOWED_PROPERTIES,
  NOT_ALLOWED_PROPERTY_TYPES,
} from './constants';
import * as loggersInterface from './loggers';
import type { EventPropertyTypes, ResolvedEventDefinition } from './schemas';
import * as schemas from './schemas';
import type { TrackerParsedConfig } from './trackerTypes';

export const resolveAsyncProperties = <T extends Record<string, JsonValue>>(
  properties: AsyncProperties<T> = {} as AsyncProperties<T>,
  callback: (properties: T) => void,
  failureCallback?: (propertyName: string, error: Error) => void
) => {
  const resolved = {} as T;
  const propertyKeys = helpers.getObjectKeys(properties);
  const propertiesLength = propertyKeys.length;

  type Deferred = {
    key: keyof T;
    promise?: Promise<T[keyof T]>;
    func?: PropertyGetter<T[keyof T]>;
  };

  const deferred = propertyKeys.reduce((accumulator, key) => {
    const propertyValue = properties[key];

    // When the value is a function we want to Promisify it
    // So we can gracefully handle if the function throws errors
    if (propertyValue && typeof propertyValue === 'function') {
      accumulator.push({ key, func: propertyValue });
      return accumulator;
    }

    // Handles when the value itself is already a Promise (object)
    // Because not all promise properties necessarily are functions
    if (propertyValue && helpers.isPromise(propertyValue)) {
      accumulator.push({ key, promise: propertyValue });
      return accumulator;
    }

    resolved[key] = propertyValue as T[keyof T & string];
    return accumulator;
  }, [] as Deferred[]);

  const check = () => {
    if (Object.keys(resolved).length === propertiesLength) {
      callback(resolved);
    }
  };

  if (deferred.length) {
    const handleSuccess = <F extends keyof T>(key: F, value: T[F]) => {
      resolved[key] = value;
      check();
    };

    const handleFailure = <F extends keyof T>(key: F, error: Error) => {
      // We set the values as null so when they get synced with the propertiesCache the original values
      // that are promises and/or functions will be overriden and a proper error dispatched
      resolved[key] = null as any;

      helpers.ensureFn(failureCallback)(key as string, error as Error);

      check();
    };

    return deferred.forEach(({ key, promise, func }) => {
      const finalPromise = func
        ? new Promise<T[keyof T]>((resolve) => resolve(func()))
        : promise;

      if (finalPromise) {
        finalPromise
          .then((value) => handleSuccess(key, value))
          .catch((error) => handleFailure(key, error));
      }
    });
  }

  return callback(resolved);
};

export const createPropertyResolver = (config: TrackerParsedConfig) => {
  let propertiesCache = { ...config.properties };

  const proxyLogger = helpers.proxyLogger({
    'tracker.client': config.clientName,
    'tracker.name': config.trackerName,
  });

  const reportFailedResolution =
    loggersInterface.createPropertyResolutionFailedLogger(
      config.logError,
      config.logWarning,
      proxyLogger(config.onError)
    );

  const isDebugEnabled =
    typeof config.debug === 'function' ? config.debug() : config.debug;

  const logWarning = loggersInterface.createWarningLogger(config.logWarning);

  // The `lastKnownEventProperties´ is a config option that allows you to carry over
  // event specific properties and mark them as "global" (instance-based) properties
  // similarly as the default properties. For example if you define the value of the option to ['email', 'someProperty']
  // Once a `track` call with these properties happens, they will be added to the property cache and carried over
  const syncEventProperties = (properties: ResolvableProperties) => {
    const lastKnownEventProperties = config.lastKnownEventProperties;

    if (lastKnownEventProperties && lastKnownEventProperties.length) {
      lastKnownEventProperties.forEach((key) => {
        const value = properties[key];

        // We do a casting here knowing `valueType` could return also 'undefined' or other types
        // But this is needed as `schemas.eventPropertyTypes` is a readonly array
        const valueType = helpers.getRealTypeOf(value) as EventPropertyTypes;

        // If the value given in the properties (user defined properties) is a non-empty value
        // It will then update the cache with this new value. If the value is the same
        // it will still be the same, even if re-assigning.
        if (schemas.eventPropertyTypes.includes(valueType)) {
          propertiesCache[key] = value;
        }
      });
    }
  };

  const createPropertiesFilter =
    (eventKey: string, eventProperties: EventProperties) =>
    (definition: ResolvedEventDefinition) => {
      // We only attempt to filter event properties if the event definition for that event is present
      // If there's no event
      const definitionProperties = helpers.getObjectKeys(definition.properties);

      const isAllowedProperty = (propertyKey: string) =>
        ALWAYS_ALLOWED_PROPERTIES.includes(propertyKey) ||
        definitionProperties.includes(propertyKey);

      const unexpectedProperties = Object.entries(eventProperties).filter(
        ([property, value]) =>
          // We should not consider values set to `null` or `undefined` as they are discarded
          // in event.ts for being nullish values
          !isAllowedProperty(property) && value !== undefined && value !== null
      );

      if (unexpectedProperties.length) {
        const warningMessage = `Event "${eventKey}" is possibly being tracked with unexpected properties. See go/unexpectedproperties`;

        logWarning(errors.genericWarning(warningMessage));

        if (isDebugEnabled) {
          config.logMessage(
            loggersInterface.createLog(
              'debug log',
              `Event "${eventKey}" has unexpected Properties.`,
              `unexpectedProperties: ${unexpectedProperties
                .map((p) => `"${p}"`)
                .join(', ')}`
            )
          );
        }
      }

      return eventProperties;
    };

  const resolveProperties = (
    eventKey: string,
    properties: EventProperties,
    callback: (properties: EventProperties) => void
  ) => {
    resolveAsyncProperties(
      propertiesCache,
      (resolved) => {
        // This method call updates the property cache adding the resolved
        // asnychronous properties to the cache and basically merges the two objects
        // with the exception that if the event propertie sent to `track` are undefined
        // We look for those properties in the cache
        propertiesCache = helpers.defaults(resolved, propertiesCache);

        // Sync the current full list of properties with the ones you want to carry over
        // We only sync the event properties and from there check what needs to be picked
        // (We compare with config.lastKnownEventProperties). Meta properties should
        // never be part of consideration as they're internal properties
        syncEventProperties(properties);

        callback(
          // This ensures that the resolved properties passed to the tracker function
          // when failed to resolve, or are undefined, or still a function, object or undefined
          // that they get passed as `null` to the event properties; this is to ensure that
          // that required and optional properties are passed always with a valid value
          helpers.reduceObject(propertiesCache)((accumulator, key) => {
            const value = propertiesCache[key];
            const valueType = helpers.getRealTypeOf(value);

            return {
              ...accumulator,
              [key]: NOT_ALLOWED_PROPERTY_TYPES.includes(valueType)
                ? null
                : value,
            };
          }) as EventProperties
        );
      },
      reportFailedResolution(eventKey)
    );
  };

  return {
    resolveProperties,
    createPropertiesFilter,
    getCache: () => helpers.shallowCopy(propertiesCache),
    addToCache: (key: string, value: PropertyResolver<JsonValue>) => {
      propertiesCache[key] = value;
    },
  };
};
